<template>
  <CCard>
    <CCardHeader>
      <CampaignViewHeader
        title="Risk at the address file"
        :disable-member-select="!selected_member"
        :selected-member-id="selected_member"
        :update-member-id-function="changeMember"
        showAmountInfo
      >
        <template slot="action">
          <div class="d-flex mt-auto">
            <CButton
              color="primary"
              class="mz-button v2-mz-btn"
              @click="handleSave"
              v-if="edit"
            >
              {{ $t("Save") }}
            </CButton>
            <div v-else class="d-flex gap-2">
              <CButton
                color="primary"
                class="mz-button v2-mz-btn"
                @click="edit = true"
              >
                {{ $t("Edit") }}
                <i class="fa fa-pen ml-3"/>
              </CButton>
              <CButton
                class="mz-button v2-mz-btn"
                color="primary"
                @click="
                  $downloadFile({
                    url: download_url
                  })
                ">
                {{ $t('Download') }}
              </CButton>
            </div>
          </div>
        </template>
      </CampaignViewHeader>
    </CCardHeader>
    <CCardBody class="pt-0">
      <CButton
        color="primary"
        variant="outline"
        class="mz-button v2-mz-btn mr-2"
        @click="filterInvalid = !filterInvalid"
      >
        <span v-if="!filterInvalid">
          {{ $t("Invalid Lines") }}
        </span>
        <span v-else>
          {{ $t("All Lines") }}
        </span>
      </CButton>
      <table-component
        class="clients-table"
        :key="`clients-table_${filterInvalid}_${clientsChangedFlag}`"
        :url="`${RISK_FILES_ENDPOINTS.list}${campaignId}/${member_id}`"
        :columns="tableColumns"
        :headings="tableHeadings"
        ref="clientsTable"
        forceFilter
        :initFilters="tableFilters"
        :settings="{ cellClasses: cellClasses }"
      >
        <template
          :slot="column.name"
          v-for="column in tableColumns"
          slot-scope="{ row }"
        >
          <CInput
            v-if="edit && !column.name.startsWith('id_')"
            v-model="row[column.name]"
            :type="column.type === 'date' ? 'datetime-local' : 'text'"
            :isValid="
              errors && errors[row.id] && errors[row.id].includes(column.name)
                ? false
                : undefined
            "
            @change="handleClientChange(row)"
            :step="1"
          />
          <div v-else-if="column.type === 'date'">
            {{ row[column.name] | formatDate }}
          </div>
          <div v-else-if="column.type === 'float64'">
            {{ $getNumericalValue(row[column.name], 0) }}
          </div>
          <div v-else>
            {{ row[column.name] }}
          </div>
        </template>
      </table-component>
    </CCardBody>
  </CCard>
</template>

<script>
import CampaignViewHeader from "@/components/CampaignViewHeader";
import {RISK_FILES_ENDPOINTS, RiskFilesServices} from "@/services/risk_files";
import {CampaignMixin} from "@/mixins/campaign";
import {MemberService} from "@/services/members";


const possibleStatuses = ["Valid", "Default", "Alerte"];

export default {
  name: "CampaignDataDetailsView",
  mixins: [CampaignMixin],
  components: {CampaignViewHeader},
  data() {
    return {
      selected_member: undefined,
      RISK_FILES_ENDPOINTS,
      download_url: '',
      members_options: [],
      fileId: this.$route.params.fileId,
      campaignId: this.$route.params.campaignId,
      member_id: this.$route.params.id,
      details: false,
      edit: false,
      filterInvalid: false,
      clientsChangedFlag: false,
      clientsChanged: {},
      errors: {},
      validation: {},
    };
  },
  async mounted() {
    const res = await MemberService.getMembers()
    this.members_options = res.map((member) => ({
      value: member.id_member,
      label: member.name,
    }));
    this.selected_member = Number(this.$route.params.id)
    this.download_url = RISK_FILES_ENDPOINTS.download_riskfile(
      this.$route.params.campaignId, this.$route.params.id)

  },
  computed: {
    tableFilters() {
      const baseFilters = {
        id_campaign: this.campaignId,
        id_file: this.fileId,
        id_member: this.member_id,
      };
      if (!this.filterInvalid) return baseFilters;
      return {
        ...baseFilters,
        row_correct: "Alerte",
      };
    },
    cellClasses() {
      const cellClassesObj = {};
      for (const col of Object.keys(this.tableHeadings)) {
        const validationColumn = this.validationColumns[col];
        cellClassesObj[col] = possibleStatuses.map((status) => {
          return {
            class: `validation-${status}`,
            condition: (row) => {
              if (['check_latitude', 'check_longitude', 'check_cli'].includes(validationColumn)
                && status === 'Default') return row[col] === 0
              return row[validationColumn] === status
            }
          };
        });
      }
      return cellClassesObj;
    },
    validationColumns() {
      return {
        uw_year: "check_year",
        annual_premiumn: "check_prime",
        coinsurance_share: "check_coinsurance",
        line_of_business: "check_category",
        lattitude: "check_latitude",
        longitude: "check_longitude",
        rooms_number: "check_rooms",
        area: "check_area",
        sums_total: "check_sums_total",
        cli: "check_cli",
      };
    },
    tableHeadings() {
      return {
        uw_year: this.$t("UW Year"),
        date_add: this.$t("Adding date"),
        annual_premiumn: this.$t("Annual premium"),
        inception_date: this.$t("Inception date"),
        resignation_date: this.$t("Resignation date"),
        cover_time: this.$t("Cover time"),
        coinsurance_share: this.$t("Coinsurance Share"),
        line_of_business: this.$t("Line of business"),
        risk_reference: this.$t("Risk reference"),
        address: this.$t("Address"),
        insee_code: this.$t("INSEE Code of risk"),
        postal_code: this.$t("Postal Code of risk"),
        city: this.$t("City of risk"),
        lattitude: this.$t("Latitude"),
        longitude: this.$t("Longitude"),
        building_type: this.$t("Building Type"),
        statue_of_accupancy: this.$t("Statue of occupancy"),
        rooms_number: this.$t("Number of main rooms in risks (residential)"),
        area: this.$t("Area (commercial risks)"),
        activity: this.$t("Activity (if commercial risks)"),
        sums_building: this.$t("Sums Insured (Building)"),
        sums_content: this.$t("Sums Insured (Content)"),
        sums_bi: this.$t("Sums Insured (BI)"),
        sums_total: this.$t("Sums Insured (Total)"),
        cli: this.$t("CCL"),
        franchise: this.$t("Fire/Terror deductible"),
        comments: this.$t("Comments"),
      };
    },
    tableColumns() {
      return Object.keys(this.tableHeadings).map((column) => ({
        name: column,
        type: this.classifyType(column),
      }));
    },
  },
  methods: {
    changeMember(member_id) {
      this.$router.push({
        name: 'Risks at the address file',
        params: {campaignId: this.$route.params.campaignId, id: member_id}
      });
    },
    handleClientChange(client) {
      this.clientsChanged[client.id] = {...client};
    },
    classifyType(column) {
      if (column.includes("date")) {
        return "date"
      }
      if (column.includes("sums")) {
        return "float64"
      }
      return "text"
    },
    async handleSave() {
      const clients = Object.values(this.clientsChanged);
      if (clients.length) {
        const {data} = await RiskFilesServices.editRiskFiles(clients, {
          id_campaign: this.campaignId,
          id_file: this.fileId,
          id_member: this.member_id,
        });
        if (!data.ok) {
          alert(this.$t("Error saving data"), {
                action: {
                    text: this.$t('Close'),
                    icon: 'close',
                    onClick: (e, toastObject) => {
                        toastObject.goAway(0);
                    }
                }
            });
          return (this.errors = data.errors);
        }
        alert(this.$t("Data saved successfully"), {
                action: {
                    text: this.$t('Close'),
                    icon: 'close',
                    onClick: (e, toastObject) => {
                        toastObject.goAway(0);
                    }
                }
            });
        this.$refs.clientsTable.$refs.table.getData();
      }
      this.edit = false;
      this.details = false;
      this.clientsChangedFlag = !this.clientsChangedFlag;
      this.clientsChanged = {};
      this.errors = {};
    },
  }
}
</script>

<style lang="scss">
.clients-table
.VueTables
.VueTables__table.mz-table
> tbody
> tr.VueTables__row {
  .validation-Default {
    color: #e87807 !important;
    background-color: #fcd4ac !important;
  }

  .validation-Alerte {
    color: #721c24 !important;
    background-color: #f8d7da !important;
  }
}

.clients-table td {
  &,
  input {
    color: inherit !important;
  }
}
</style>
